/**
 * A single Y-axis entry row with column selector, aggregation selector, and optional remove button.
 */

import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AggregationFunction, ColumnTypeInfo } from '../types/chartTypes';
import {
  AGGREGATION_FUNCTIONS,
  AGGREGATION_LABELS,
} from '../types/chartTypes';

type ColumnOption = {
  value: string;
  label: string;
};

type YAxisEntryRowProps = {
  readonly index: number;
  readonly column: string;
  readonly aggregation: AggregationFunction;
  readonly numericColumns: ColumnTypeInfo[];
  readonly allColumnOptions: ColumnOption[];
  readonly showRemove: boolean;
  readonly onColumnChange: (value: string) => void;
  readonly onAggregationChange: (value: AggregationFunction) => void;
  readonly onRemove: () => void;
};

export function YAxisEntryRow({
  index,
  column,
  aggregation,
  numericColumns,
  allColumnOptions,
  showRemove,
  onColumnChange,
  onAggregationChange,
  onRemove,
}: YAxisEntryRowProps) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-end">
      <Select
        value={column}
        onValueChange={onColumnChange}
      >
        <SelectTrigger data-testid={`y-column-select-${index}`}>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {numericColumns.length > 0 ? (
            numericColumns.map((col) => (
              <SelectItem key={col.key} value={col.key}>
                {col.name}
              </SelectItem>
            ))
          ) : (
            allColumnOptions.map((col) => (
              <SelectItem key={col.value} value={col.value}>
                {col.label}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      <Select
        value={aggregation}
        onValueChange={(value) => onAggregationChange(value as AggregationFunction)}
      >
        <SelectTrigger data-testid={`aggregation-select-${index}`} className="w-[100px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {AGGREGATION_FUNCTIONS.map((agg) => (
            <SelectItem key={agg} value={agg}>
              {AGGREGATION_LABELS[agg]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {showRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={onRemove}
          data-testid={`y-remove-${index}`}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : (
        <div className="w-9" />
      )}
    </div>
  );
}
